import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { submitFormData } from '../../../pages/api';
import apiClient from '../../../pages/keycloak/axiosInstance';

const Form_7 = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const savedData = sessionStorage.getItem('CellToRibbonPeelStrengthF7formData');
  const [formData, setFormData] = useState(savedData ? JSON.parse(savedData) : { date: '', shift: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lines, setLines] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const inspectionDate = sessionStorage.getItem('inspectionDate');
    const inspectionShift = sessionStorage.getItem('inspectionShift');
    const metadata = JSON.parse(sessionStorage.getItem('metadata') || '{}');

    const lineData = metadata?.lines?.map((line) => ({
      name: line.Lines,
      fieldName: String(line.Lines).toLowerCase().replace(/\s/g, '') + 'file',
    })) || [];
    setLines(lineData);

    setFormData((prev) => ({
      ...prev,
      date: inspectionDate || prev.date,
      shift: inspectionShift || prev.shift,
      ...lineData.reduce((acc, line) => ({ ...acc, [line.fieldName]: [] }), {}),
    }));

    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchUploadedFiles = async () => {
      const projectId = localStorage.getItem('selectedProject');
      const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
      if (!projectId || !inspectionDate || !inspectionSupplier || !inspectionShift) return;

      const fetchedFiles = {};
      await Promise.all(
        lineData.map(async (line) => {
          try {
            const response = await apiClient.get(`get_uploaded_inspection_files`, {
              params: {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                formName: 'CellToRibbonPeelStrength',
                inputFieldId: line.fieldName,
              },
            });
            fetchedFiles[line.fieldName] = response.data.files || [];
          } catch (error) {
            console.error(`Error fetching files for ${line.fieldName}:`, error);
            fetchedFiles[line.fieldName] = [];
          }
        })
      );
      setUploadedFiles(fetchedFiles);
    };

    fetchUploadedFiles();
  }, []);

  // useEffect(() => {
  //   sessionStorage.setItem('CellToRibbonPeelStrengthF7formData', JSON.stringify(formData));
  // }, [formData]);

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
    });

  const handleFileChange = async (e, fieldName) => {
    const files = Array.from(e.target.files);
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
    ];

    const invalidFiles = files.filter((file) => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: 'Only PDF, JPEG, PNG, Excel, or Word files allowed.',
      }));
      return;
    }

    try {
      const fileData = await Promise.all(
        files.map(async (file) => ({
          file: await convertToBase64(file),
          filename: file.name,
        }))
      );

      setFormData((prev) => ({
        ...prev,
        [fieldName]: fileData,
      }));
      setErrors((prev) => ({ ...prev, [fieldName]: '' }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [fieldName]: 'Error processing files.' }));
    }
  };

  const getFileLinkProps = (file) => {
    const extension = file.filename.split('.').pop().toLowerCase();
    const viewableTypes = ['pdf', 'jpeg', 'jpg', 'png'];
    return viewableTypes.includes(extension)
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : { download: true };
  };

  const isValidFileUrl = (url, filename) => {
    if (!url || !url.trim()) return false;
    const extension = filename.split('.').pop().toLowerCase();
    return url.toLowerCase().endsWith(`.${extension}`) || url.includes(filename);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    const hasAtLeastOneFile = lines.some((line) => formData[line.fieldName]?.length > 0);
    if (!hasAtLeastOneFile) {
      lines.forEach((line) => {
        newErrors[line.fieldName] = `${line.name} file is required`;
      });
      setErrors(newErrors);
      alert('At least one file must be uploaded.');
      return;
    }

    setIsLoading(true);
    try {
      const projectId = localStorage.getItem('selectedProject');
      const inspectionDate = sessionStorage.getItem('inspectionDate');
      const inspectionShift = sessionStorage.getItem('inspectionShift');
      const inspectorName = sessionStorage.getItem('username');
      const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

      const uploadedFilesData = [];
      for (const line of lines) {
        if (formData[line.fieldName]?.length > 0) {
          const filesData = formData[line.fieldName].map((fileData) => ({
            inputFieldId: line.fieldName,
            file: fileData.file,
            filename: fileData.filename,
          }));

          const uploadResponse = await apiClient.post(`upload_inspection_files`, {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            formName: 'CellToRibbonPeelStrength',
            files: filesData,
          });

          uploadedFilesData.push(...filesData);

          const response = await apiClient.get(`get_uploaded_inspection_files`, {
            params: {
              projectId,
              inspectionSupplier,
              inspectionDate,
              inspectionShift,
              formName: 'CellToRibbonPeelStrength',
              inputFieldId: line.fieldName,
            },
          });
          setUploadedFiles((prev) => ({
            ...prev,
            [line.fieldName]: response.data.files || [],
          }));
        }
      }

      // Primary structure: Send only the first file per line
      const filesObj = lines.reduce(
        (acc, line) => ({
          ...acc,
          [line.name]: uploadedFiles[line.fieldName]?.length > 0
            ? { filename: uploadedFiles[line.fieldName][0].filename, url: uploadedFiles[line.fieldName][0].url }
            : null,
        }),
        {}
      );

      /*
      // Alternative structure: Flat list of files (uncomment to test)
      const filesObj = uploadedFilesData.map(file => ({
        filename: file.filename,
        inputFieldId: file.inputFieldId
      }));
      */

      const payload = {
        projectId,
        inspectionSupplier,
        inspectionDate,
        inspectionShift,
        inspectorName,
        status: 'completed',
        CellToRibbonPeelStrength: {files: filesObj},
      };

      await submitFormData(payload);

      setFormData((prev) => ({
        ...prev,
        ...lines.reduce((acc, line) => ({ ...acc, [line.fieldName]: [] }), {}),
      }));
      alert('Form submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error.response?.data || error.message);
      alert('Failed to submit form: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full mx-auto p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold text-center mb-8 text-gray-800 bg-gray-100 py-3 rounded-lg">
        Cell to Ribbon Peel Strength
      </h2>
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
            <input
              type="date"
              value={formData.date}
              readOnly
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Shift</label>
            <input
              type="text"
              value={formData.shift}
              readOnly
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-600"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lines.map((line) => (
            <div key={line.name} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{line.name} File Upload</label>
              <div className="relative">
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileChange(e, line.fieldName)}
                  className={`w-full p-3 border rounded-lg text-sm ${
                    errors[line.fieldName] ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-gray-200`}
                />
                {formData[line.fieldName]?.length > 0 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 text-xs font-medium">
                    {/* ✓ {formData[line.fieldName].length} file(s) selected */}
                  </span>
                )}
              </div>
              {errors[line.fieldName] && <p className="text-red-500 text-xs mt-1">{errors[line.fieldName]}</p>}
              {formData[line.fieldName]?.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-semibold">Selected Files:</p>
                  <ul className="text-xs text-gray-600">
                    {formData[line.fieldName].map((file, index) => (
                      <li key={index} className="truncate" title={file.filename}>
                        {file.filename}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {uploadedFiles[line.fieldName]?.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-semibold">Previously Uploaded:</p>
                  <ul className="text-xs text-gray-600">
                    {uploadedFiles[line.fieldName].map((file, index) => {
                      return (
                        <li key={index} className="truncate" title={file.filename}>
                          {file.url && isValidFileUrl(file.url, file.filename) ? (
                            <a
                              href={file.url}
                              {...getFileLinkProps(file)}
                              className="text-blue-600 hover:underline"
                            >
                              {index + 1}. {file.filename}
                            </a>
                          ) : (
                            <span className="text-gray-500">
                              {index + 1}. {file.filename} (Invalid or missing URL)
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6">
          <button
            type="button"
            onClick={() => navigate('/inspections/section2/form6')}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Previous
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className={`px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/inspections/section2/form8')}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default Form_7;